import { useQuery } from '@apollo/client'
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material'
import { GetUsersByEmailQuery } from '__generated__/graphql'
import { FormikProps } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { GetUsersByEmail } from 'recruiter/graphql/users.gql'
import { GrantClientRoleFormValues } from './AddClient'

const LIMIT = 4

export interface UsersByEmailProps
    extends Omit<
        AutocompleteProps<
            GetUsersByEmailQuery['getUsersByEmail']['data'][number],
            false,
            false,
            false
        >,
        'options' | 'renderInput'
    > {
    error?: boolean
    helperText?: string
    placeholder?: string
    label?: string
    currentUser:
        | GetUsersByEmailQuery['getUsersByEmail']['data'][number]
        | undefined
    updateSelectedUser: React.Dispatch<
        React.SetStateAction<
            GetUsersByEmailQuery['getUsersByEmail']['data'][number] | undefined
        >
    >
    serverColorOverride: boolean
    formik: FormikProps<GrantClientRoleFormValues>
}

const getNewUserOption = (email: string) => {
    return {
        id: 'unknown',
        email: email,
        firstName: '',
        lastName: '',
    }
}

const UsersByEmail: React.FC<UsersByEmailProps> = ({
    error,
    helperText,
    size,
    placeholder,
    currentUser,
    serverColorOverride,
    updateSelectedUser,
    label = 'Email',
    formik,
    ...props
}) => {
    const [options, setOptions] = useState<
        GetUsersByEmailQuery['getUsersByEmail']['data'][number][]
    >([getNewUserOption('')])

    const [debounceEmail, setDebouncedEmail] = useState(formik.values.email)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedEmail(formik.values.email)
            nextPageToFetch.current = 2
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [formik.values.email])

    const { data, fetchMore, loading } = useQuery(GetUsersByEmail, {
        variables: { email: debounceEmail, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: !open || !debounceEmail || debounceEmail.trim() === '',
        onCompleted: (data) => {
            setOptions([
                ...(currentUser?.email &&
                currentUser?.email !== '' &&
                !data.getUsersByEmail.data.some(
                    (user) => user.email === currentUser?.email
                )
                    ? [getNewUserOption(currentUser?.email ?? '')]
                    : []),
                ...(data?.getUsersByEmail?.data || []),
            ])
        },
    })

    const loadMoreData = useCallback(() => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                email: debounceEmail,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.getUsersByEmail?.data ||
                    !fetchMoreResult?.getUsersByEmail?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedUsersData = Array.from(
                    previousData.getUsersByEmail.data
                )
                const pageLimit =
                    previousData.getUsersByEmail?.pageInfo.pageTotal ?? 0
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.getUsersByEmail.data.length;
                    i++
                ) {
                    updatedUsersData[(page - 1) * limit + i] =
                        fetchMoreResult.getUsersByEmail.data[i]
                }
                const newCache = {
                    ...previousData,
                    usersByEmail: {
                        ...fetchMoreResult.getUsersByEmail,
                        data: updatedUsersData,
                    },
                }
                if (
                    fetchMoreResult?.getUsersByEmail?.pageInfo.itemTotal &&
                    fetchMoreResult.getUsersByEmail.pageInfo.itemTotal ===
                        updatedUsersData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMore, debounceEmail])

    const observerCallback = useCallback(
        (entries) => {
            if (formik.values.email.trim() === '') return
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        },
        [loadMoreData, formik.values.email]
    )

    const observedElementRef = useRef<HTMLLIElement | null>(null)
    const nextPageToFetch = useRef<number>(2)
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver(observerCallback)
    )

    useEffect(() => {
        observer.current = new IntersectionObserver(observerCallback)
        // update which element we are observing as data changes
        if (observedElementRef.current) {
            observer.current?.observe(observedElementRef.current)
        }

        return () => {
            if (observer.current) observer.current.disconnect()
        }
        // open is a dependency because we lose the observedElementRef when the dropdown closes and need to re-observe it once we open again
    }, [observerCallback, open, data?.getUsersByEmail.data])

    return (
        <Autocomplete
            fullWidth
            autoSelect
            filterOptions={(x) => x}
            disablePortal
            getOptionLabel={(option) => option.email ?? ''}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            open={open}
            loading={loading}
            {...props}
            isOptionEqualToValue={(option, value) => {
                return (option?.email ?? '') === (value?.email ?? '')
            }}
            renderOption={(props, option, state, ownerState) => {
                const index = state.index
                const totalOptions = ownerState.options.length
                const isObserver = index === totalOptions - 1
                return (
                    <li
                        {...props}
                        key={typeof option === 'string' ? option : option.id}
                        ref={isObserver ? observedElementRef : null}
                    >
                        {typeof option === 'string' ? option : option.email}
                    </li>
                )
            }}
            onInputChange={(e, value, reason) => {
                if (reason === 'clear') {
                    formik.setFieldValue('email', '')
                    updateSelectedUser(undefined)
                } else {
                    const option = options.find(
                        (option) => option?.email === value
                    )
                    updateSelectedUser(option ?? getNewUserOption(value))
                    formik.setFieldValue('email', option?.email ?? value)
                }
            }}
            options={options}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant={'standard'}
                    required
                    fullWidth
                    name="email"
                    size={size}
                    label={label}
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                    InputLabelProps={{ shrink: true }}
                    FormHelperTextProps={{
                        style: serverColorOverride
                            ? { color: 'inherit' }
                            : { color: 'red' },
                    }}
                />
            )}
        />
    )
}

export default UsersByEmail
